import { useEffect, useState } from 'react';
import { MFAEnrollmentStatus, User } from '@tcc/shared-data-lib';
import { useMutation } from '@apollo/client/react';
import { getGraphQlClient } from '../graphql/graphqlClient';
import { updateUserMfaEnrollmentStatus } from '../graphql/mutations/updateUserMfaEnrollmentStatus';
import { authorizeWithPassword } from '../requesters/authorize';
import { setCookie, getCookie } from '../utils/cookies';
import { AuthorizationError } from '../types/AuthorizationError';
import { reportError } from '../utils/datadog';

type Enable2FAResponse = {
  success: boolean;
  error?: 'password' | 'unknown';
};

interface useMFADialogProps {
  user: User;
  featureFlags: Record<string, boolean>;
}

export function useMFADialog({ user, featureFlags }: useMFADialogProps) {
  const client = getGraphQlClient({ isUApi: true });
  const [showMFADialog, setShowMFADialog] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [updateMfaStatus] = useMutation(updateUserMfaEnrollmentStatus, {
    client,
  });

  useEffect(() => {
    const isMFAFlagActive = featureFlags?.['web.multi-factor-authentication'];
    if (isMFAFlagActive) {
      const isMFADismissed = getCookie('mfa_dialog_dismissed') === 'true';
      if (!isMFADismissed) {
        setShowMFADialog(
          user.details.preferences.mfaEnrollmentStatus ===
            MFAEnrollmentStatus.ELIGIBLE,
        );
      }
    }
  }, [featureFlags, user]);

  const handleMFACancel = () => {
    setCookie({
      value: 'true',
      expiresInMinutes: 4320,
      key: 'mfa_dialog_dismissed',
    });
    setShowMFADialog(false);
  };

  const onEnableMFA = async (password: string): Promise<Enable2FAResponse> => {
    try {
      setIsEnabling(true);
      await authorizeWithPassword(user.details.email, password);
      await updateMfaStatus({
        variables: {
          input: {
            userId: user.uuid,
            mfaEnrollmentStatus: MFAEnrollmentStatus.ENABLED,
          }
        }
      });
      return {
        success: true,
      };
    } catch (error) {
      const status = error instanceof AuthorizationError ? error.status : undefined;
      // A 401 is an expected "wrong password" outcome; anything else is an
      // unexpected failure (network, GraphQL mutation, etc.) worth tracking.
      if (status !== 401) {
        reportError('FieldViewMfaEnableError', error, { hook: 'useMFADialog', userId: user.uuid });
      }
      return {
        success: false,
        error: status === 401 ? 'password' : 'unknown'
      };
    } finally {
      setIsEnabling(false);
    }
  };

  return {
    showMFADialog,
    setShowMFADialog,
    handleMFACancel,
    onEnableMFA,
    isEnabling,
  };
}
